import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { EkycStatus } from '../../core/enum/ekyc-status.enum';
import { Role } from '../../core/enum/role.enum';
import { UserProfile } from '../../core/models/user/user-profile';
import { AccountService } from '../../core/services/api/account.service';
import { ErrorService } from '../../core/services/error.service';
import { GtmDataLayerService } from '../../core/services/gtm-data-layer.service';
import { StorageService } from '../../core/services/storage.service';
import { BlockUiService } from '../../shared/services/blockUi/block-ui.service';
import { PopupService } from '../../shared/services/popup/popup.service';
import { isPlatformBrowser } from '@angular/common';
import { PostLoginInterruptService } from '../../core/services/post-login-interrupt/post-login-interrupt.service';

@Component({
    selector: 'app-login-behalf-redirection',
    templateUrl: './login-behalf-redirection.component.html',
    styleUrls: ['./login-behalf-redirection.component.scss'],
    standalone: false
})
export class LoginBehalfRedirectionComponent implements OnInit {
  userId: string;
  token: string;

  constructor(
    private activatedRoute: ActivatedRoute,
    private accountService: AccountService,
    private errorService: ErrorService,
    private router: Router,
    private popupService: PopupService,
    private interruptService: PostLoginInterruptService,
    private blockUiService: BlockUiService,
    private storageService: StorageService,
    private _gtmDataLayerService: GtmDataLayerService,
    @Inject(PLATFORM_ID) private platform: Object,
  ) {}

  ngOnInit(): void {
    this.userId = this.activatedRoute.snapshot.queryParams.userId;
    this.token = this.activatedRoute.snapshot.queryParams.token;

    if (isPlatformBrowser(this.platform)) {
      this.accountService.loginOnBehalf(this.userId, this.token).subscribe({
        next: (data) => {
          let role = data.role;

          if (!role || role == Role.SuperAdmin || role == Role.Admin) {
            this.popupService.alert('common.unauthorisedAccess');
            this.accountService.logout().subscribe();
            return;
          }

          // INC-329: the third call site follows the same rule as the login screen — NO POPUP
          // DURING THE REDIRECT. It used to open the nudge here, full-screen, on the legacy
          // `isForceUpdateUserProfile` flag, in the middle of a redirect the investor did not
          // ask to be interrupted in.
          //
          // It raises nothing now, and it does not need to: the post-landing trigger in
          // AppComponent reads the Flagged predicate from the profile endpoint on every app
          // init, so a flagged investor arriving this way meets the interrupt once they have
          // landed, in the same frame as everyone else. Note that this DOES quietly widen who
          // is nudged on this path from the legacy flag to the full predicate — but only in
          // the same direction the ordinary login path already moved (INC-263), and the
          // predicate is a superset, so nobody who was nudged before stops being nudged.
          this.interruptService.startSession();
          this.checkSignUpFlow(role, data);
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
    }
  }

  checkSignUpFlow(role: Role, user?: UserProfile) {
    this.accountService
      .getSignUpFlowStatus()
      .pipe(finalize(() => this.blockUiService.close()))
      .subscribe({
        next: (flowData) => {
          if (flowData && !flowData.isCompleted) {
            let roleText = Role[role].split(/(?=[A-Z])/);
            let roleRoute = roleText.join('-').toLowerCase();
            this.router.navigate(['/sign-up', roleRoute]);
            this.accountService.currentUser.next(null);
            return;
          }

          this.storageService.role = role;
          this.accountService.profile(true).subscribe({
            next: () => {
              this.router.navigate(['/']);
            },
            error: (err) => {
              this.errorService.showError(err);
            },
          });
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }
}
